<script setup lang="ts">
import { FuelTypeCount } from '@/types/statistics'
import { useMapStore } from '~/stores/map'
import { useVehicleFleetOnFuelTypeRatio } from '~/composables/statisticsData'
import { HeatmapData, MapAreaTypes } from '~/types/map'
import { useStatisticsStore } from '~/stores/statistics'
import FilterSetting from '~/components/data-filters/FilterSetting.vue'
import VehicleCount from '~/components/region-statistics/VehicleCount.vue'
import VehicleForecast from '~/components/region-statistics/VehicleForecast.vue'
import { createSlug } from '~/stores/utils/filter-util'
const {
  data: vehicleDataSweden,
  filteredData: filteredVehicleData,
  isReady,
} = useVehicleCountDataFetch<VehicleCountData[]>(undefined, undefined, true)
const mapStore = useMapStore()
mapStore.resetHeatmapSettings()
const { keyValueOptions: areaTypeOptions } = await useKeyTranslator('areaType')
const statisticsStore = useStatisticsStore()

const filterSettings = computed(() => statisticsStore.filterSettings)
const activeFuelType = computed(
  () => statisticsStore.filters.find((f) => f.filterIdentifier === 'fuelType')?.values[0]
)

onUnmounted(() => {
  mapStore.resetHeatmapSettings()
})

const { data: vehicleDataMunicipality } = useVehicleFleetOnFuelTypeRatio('municipality')
const { data: vehicleDataCounty } = useVehicleFleetOnFuelTypeRatio('county')

watch(
  vehicleDataMunicipality,
  (newValue) => {
    if (!newValue) return
    parseHeatmapdata()
  },
  { immediate: true }
)

function parseHeatmapdata() {
  const municipalityData: HeatmapData[] = vehicleDataMunicipality.value.map((municipality) => ({
    id: municipality.municipality_code,
    value: ((municipality.total_count[activeFuelType.value as keyof FuelTypeCount] as number) *
      100) as number,
  }))
  mapStore.setHeatmapMunicipalityData(municipalityData)

  const countyData: HeatmapData[] = vehicleDataCounty.value.map((county) => ({
    id: county.county_code,
    value: ((county.total_count[activeFuelType.value as keyof FuelTypeCount] as number) *
      100) as number,
  }))
  mapStore.setHeatmapCountyData(countyData)
}

const areaType = ref<MapAreaTypes>('municipality')
watch(areaType, (newValue) => {
  if (!newValue) return
  updateHeatmapSettings()
})

function updateHeatmapSettings() {
  mapStore.setHeatmapSettings({ dataSuffix: '%', areaType: areaType.value })
}
updateHeatmapSettings()

</script>
<template>
  <fieldset class="mb-5">
    <legend class="flex align-items-center px-2 pb-2">
      Välj vilka områden som ska visualiseras på kartan
    </legend>
    <div class="flex">
      <div v-for="option in areaTypeOptions" :key="option.value" class="flex ml-2">
        <RadioButton
          v-model="areaType"
          :value="option.value"
          :input-id="createSlug(option.value)"
        />
        <label class="ml-2" :for="createSlug(option.value)">{{ option.label }}</label>
      </div>
    </div>
  </fieldset>
  <div class="my-3">
    <AppHeading title="Filterinställningar" type="h3" size="large" />
    <div class="mt-3">
      <FilterSetting content-type="statistics" :setting="filterSettings.vehicleTypeFromFleetData" />
    </div>
    <div class="mt-3">
      <FilterSetting content-type="statistics" :setting="filterSettings.fuelType" />
    </div>
  </div>
  <VehicleCount :hide-filters="true" />
</template>
